import React, { ComponentType, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import moment from 'moment';
import { RootDispatch, RootState } from 'store/types';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import { Route, Switch, RouteComponentProps, Redirect } from 'react-router';
import { useRoutes } from 'hooks/useRoutes';
import { PreRegistrationPaths } from 'lib/constants/preRegister';
import { RouteData } from './types';
import { decodeJWT } from 'lib/preRegistration';
import { Alert } from 'components/Alert';
import { useHistory } from 'react-router-dom';
import CheckIn from './CheckIn';
import PreRegisterationChecklist from './Checklist';
import AdditionalInfo from './AdditionalInfo';
import PersonalInformation from './PersonalInformation';
import UploadPhotoId from './UploadPhotoId';
import GuarantorInformation from './GuarantorInformation';
import Complete from './Complete';
import BadAttempts from './BadAttempts';
import Payment from './Payment';
import ConsentForm from './ConsentForm';
import MSPQForm from './MSPQForm';
import LoggedOut from './LoggedOut';
import PreRegisterationInsurance from './Insurance';
import ExceptionScreen from './ExceptionScreen';
import { useQuery } from '@apollo/react-hooks';
import { GetPreRegistrationInfo } from 'services/orchestration/GetPreRegistrationInfo';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import * as actions from 'store/preRegistration/actions';
import { PreRegistration } from 'store/preRegistration/types';
import { Profile } from 'store/profile/types';
import { CenteredTypography } from './styled';
import { FontSize } from 'modules/styles/variables';
import {
  PreRegistrationEventIdSelector,
  PreRegistrationJwtTokenSelector
} from 'store/preRegistration/selectors';
import { CenteredSpinner } from 'components/UI/Spinner/SpinnerContainer';
import Screen from 'components/UI/Layout/Screen';
// import { mockData } from './mockData'; // TODO UNDO

export const preRegistractionFormRoutes: RouteData[] = [
  {
    path: PreRegistrationPaths.CHECK_LIST,
    component: PreRegisterationChecklist,
    componentName: 'PreRegisterationPreRegisterationChecklist',
    title: 'Pre Registration Checklist'
  },
  {
    path: PreRegistrationPaths.PATIENT,
    component: PersonalInformation,
    componentName: 'PreRegisterationPersonalInformation',
    title: 'Connect Care Patients'
  },
  {
    path: PreRegistrationPaths.ADDITIONAL,
    component: AdditionalInfo,
    componentName: 'PreRegisterationAdditionalInfo',
    title: 'Connect Care Additional Information'
  },
  {
    path: PreRegistrationPaths.PHOTO_ID,
    component: UploadPhotoId,
    componentName: 'UploadPhotoId',
    title: 'PreRegisterationConnect Care Patients'
  },
  {
    path: PreRegistrationPaths.GUARANTOR,
    component: GuarantorInformation,
    componentName: 'PreRegisterationGuarantorInformation'
  },
  {
    path: PreRegistrationPaths.PAYMENT,
    component: Payment,
    componentName: 'Payment'
  },
  {
    path: PreRegistrationPaths.COMPLETE,
    component: Complete,
    componentName: 'PreRegisterationComplete'
  },
  {
    path: PreRegistrationPaths.CONSENT_FORM,
    component: ConsentForm,
    componentName: 'PreRegisterationConsentForm'
  },
  {
    path: PreRegistrationPaths.MSPQ,
    component: MSPQForm,
    componentName: 'PreRegisterationMSPQForm'
  },
  {
    path: PreRegistrationPaths.LOGGED_OUT,
    component: LoggedOut,
    componentName: 'PreRegisterationLoggedOut'
  },
  {
    path: PreRegistrationPaths.INSURANCE,
    component: PreRegisterationInsurance,
    componentName: 'PreRegisterationInsurance'
  }
];
const checkInRoutes: RouteData[] = [
  {
    path: PreRegistrationPaths.LOGGED_OUT,
    component: LoggedOut,
    componentName: 'PreRegisterationLoggedOut'
  },
  {
    path: PreRegistrationPaths.FAILED,
    component: BadAttempts,
    componentName: 'PreRegisterationBadAttempts'
  },
  {
    path: '/:eventId',
    exact: true,
    component: CheckIn,
    componentName: 'CheckIn',
    title: 'Pre Registration Check-in'
  }
];

export interface PreRegistrationRouterProps extends RouteComponentProps {
  dispatch: RootDispatch;
  PreRegistrationEventId: string;
  jwt: string | null;
  currentUrl: string;
  referringUrl: string;
}

const queryOptions = {
  notifyOnNetworkStatusChange: true,
  context: {
    clientName: 'orchestration'
  }
};

export const PreRegistrationRouter = ({
  PreRegistrationEventId: eventId,
  jwt,
  currentUrl,
  referringUrl
}: PreRegistrationRouterProps) => {
  const dispatch = useDispatch();
  let routeList = preRegistractionFormRoutes;
  // We only want to show the login if they arent logged in.
  // Otherwise we can just redirect them to the checklist again
  if (!jwt) {
    routeList = checkInRoutes;
  }
  const routes = useRoutes(routeList);
  const [expireTimer, setExpireTimer] = useState<number>();
  const history = useHistory();

  const { data, loading, error } = useQuery(GetPreRegistrationInfo, {
    variables: { eventId }, // TODO get this from somewhere
    ...queryOptions,
    context: {
      ...queryOptions.context,
      headers: {
        'x-prereg-event-id': eventId,
        Authorization: `Bearer ${jwt}`
      }
    },
    skip: !eventId
  });

  // TODO remove mock data when R1 is up
  // const data = mockData5;
  // const loading = false;
  // const error = null;

  const getUserData = (preRegistration: PreRegistration): Profile => {
    const consumer = { ...preRegistration.patientInfo, consumerId: preRegistration.patientInfo.id };
    const insurances = preRegistration.visitDetail?.insurance;
    const grantors = preRegistration.patientInfo;
    return { consumer, grantors, insurances };
  };

  const checkAppointmentRescheduled = (startDate: string) => {
    const endOfVisitDate = moment(startDate).endOf('day');
    const endOfYesterday = moment()
      .subtract(1, 'days')
      .endOf('day');
    const diff = endOfVisitDate.diff(endOfYesterday, 'days');
    return diff > 8 || moment() > endOfVisitDate;
  };

  const { complete: flowCompleted, linkExpired, cancelled, visitDetail } =
    data?.preRegistration || {};
  const isRescheduled =
    !!visitDetail?.startDate && checkAppointmentRescheduled(visitDetail.startDate);

  // Observables hooked into the store
  useEffect(() => {
    if (loading) {
      dispatch({ type: actions.PRE_REGISTRATION });
    }
  }, [loading]);

  useEffect(() => {
    if (error) {
      dispatch({ type: actions.PRE_REGISTRATION_FAIL, error });
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      dispatch({ type: actions.PRE_REGISTRATION_SUCCESS, payload: { data } });
      const userData = getUserData(data.preRegistration);
      const formStartData: AmplitudeEventData = {
        currentUrl,
        referringUrl,
        /* eslint-disable-next-line @typescript-eslint/camelcase */
        form_location: data.preRegistration?.visitDetail?.location?.name || ''
      };
      analyticsService.identifyUser(userData);
      const shouldFireEvent = !(
        flowCompleted ||
        linkExpired ||
        cancelled ||
        isRescheduled ||
        window.sessionStorage.getItem('preRegFormStart')
      );
      if (shouldFireEvent) {
        analyticsService.logEvent(AnalyticsEvent.HospitalFormStart, formStartData);
        window.sessionStorage.setItem('preRegFormStart', 'true');
      }
    }
  }, [data]);

  const handleTimeout = () => {
    Alert.alert('Expired Token', 'Please log in again.');
    dispatch({ type: actions.PRE_REGISTRATION_RESET });
    window.sessionStorage.setItem('PRE_REG', '');
    history.push(`/pre-reg/logged-out`);
  };

  useEffect(() => {
    if (jwt) {
      const token = decodeJWT(jwt);
      if (token) {
        const now = Date.now();
        const exp = token.exp * 1000;

        if (now < exp) {
          const timer = window.setTimeout(handleTimeout, exp - now);
          setExpireTimer(timer);
        } else {
          handleTimeout();
        }
      } else {
        Alert.alert('Invalid token', 'Please log in again.');
      }
    }
    return () => {
      if (expireTimer) {
        window.clearTimeout(expireTimer);
      }
    };
  }, [jwt]);

  if (loading) {
    return (
      <Screen safe>
        <CenteredSpinner opacity={0.8} />
      </Screen>
    );
  }

  if (error) {
    return (
      <Screen data-testid="routerNoData" safe>
        <CenteredTypography fontSize={FontSize.large}>
          Error in loading. Please try later
        </CenteredTypography>
      </Screen>
    );
  }

  if (flowCompleted) {
    return <Complete complete={flowCompleted} />;
  }
  if (linkExpired || cancelled || isRescheduled) {
    return <ExceptionScreen complete={flowCompleted} />;
  }

  return (
    <Switch>
      {routes.map(({ component: Component, ...r }) => (
        <Route
          key={r.path}
          path={r.path}
          exact={r.exact || false}
          render={props => (
            <>
              <Component routes={routes} {...r} {...props} />
            </>
          )}
        />
      ))}
      <Route render={() => <Redirect to="/pre-reg/checklist" />} />
    </Switch>
  );
};

const mapStateToProps = (state: RootState) => ({
  PreRegistrationEventId: PreRegistrationEventIdSelector(state),
  jwt: PreRegistrationJwtTokenSelector(state),
  currentUrl: currentLocationPathNameSelector(state),
  referringUrl: previousLocationPathNameSelector(state)
});

export default connect(mapStateToProps)(PreRegistrationRouter as ComponentType);
